import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, CheckCircle2, XCircle, Clock3, CalendarDays } from "lucide-react";

const LEAVE_TYPES = [
  "Casual Leave", "Sick Leave", "Earned Leave",
  "Work From Home", "Comp Off", "Maternity Leave", "Paternity Leave", "Half Day",
];

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
}

export default function Leave() {
  const { user } = useAuth();
  const isReviewer = user?.role === "admin" || user?.role === "hr";
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [balance, setBalance] = useState({});
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ leave_type: "Casual Leave", from_date: "", to_date: "", reason: "" });

  const load = async () => {
    if (user?.employee_id) {
      const [m, b] = await Promise.all([api.get("/leaves/mine"), api.get("/leaves/balance")]);
      setMine(m.data); setBalance(b.data);
    }
    if (isReviewer) {
      const a = await api.get("/leaves/all");
      setAll(a.data);
    }
  };
  useEffect(() => { load(); }, [user?.employee_id, isReviewer]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.from_date || !form.to_date) { toast.error("Pick both dates"); return; }
    setBusy(true);
    try {
      await api.post("/leaves/apply", form);
      toast.success("Leave submitted");
      setShowForm(false);
      setForm({ leave_type: "Casual Leave", from_date: "", to_date: "", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const decide = async (id, status) => {
    try {
      await api.post(`/leaves/${id}/decide`, { status });
      toast.success(`Leave ${status.toLowerCase()}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const statusBadge = (s) => {
    if (s === "Approved") return <Badge className="bg-emerald-50 text-emerald-700 border-0 hover:bg-emerald-50">Approved</Badge>;
    if (s === "Rejected") return <Badge className="bg-red-50 text-red-700 border-0 hover:bg-red-50">Rejected</Badge>;
    return <Badge className="bg-orange-50 text-orange-700 border-0 hover:bg-orange-50">Pending</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="leave-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">Leave management</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Time away, transparent</h1>
          <p className="mt-1 text-sm text-slate-500">Apply, track and — if you're HR — approve leave requests.</p>
        </div>
        {user?.employee_id && (
          <Button onClick={() => setShowForm(v => !v)} className="bg-[#DC2626] hover:bg-[#B91C1C] rounded-lg" data-testid="apply-leave-btn">
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.8} />
            {showForm ? "Close" : "Apply leave"}
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="p-6 rounded-xl border-slate-200/60" data-testid="leave-form-card">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label>Leave type</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>From</Label>
              <Input type="date" value={form.from_date} onChange={e => setForm({ ...form, from_date: e.target.value })} data-testid="leave-from" />
            </div>
            <div className="space-y-1.5">
              <Label>To</Label>
              <Input type="date" value={form.to_date} onChange={e => setForm({ ...form, to_date: e.target.value })} data-testid="leave-to" />
            </div>
            <div className="space-y-1.5 md:col-span-4">
              <Label>Reason</Label>
              <Textarea rows={3} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Short reason so your manager can approve quickly" data-testid="leave-reason" />
            </div>
            <div className="md:col-span-4 flex justify-end">
              <Button disabled={busy} type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="leave-submit">Submit request</Button>
            </div>
          </form>
        </Card>
      )}

      {user?.employee_id && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(balance.allotted || {}).map(([k, total]) => {
            const u = (balance.used || {})[k] || 0;
            const rem = Math.max(0, total - u);
            const pct = total ? (u / total) * 100 : 0;
            return (
              <Card key={k} className="p-4 rounded-xl border-slate-200/60">
                <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 capitalize">{k.replace(/_/g, " ")}</div>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="font-display text-2xl font-bold text-slate-900">{rem}</span>
                  <span className="text-xs text-slate-500">/ {total}</span>
                </div>
                <Progress value={pct} className="h-1 mt-2.5" />
              </Card>
            );
          })}
        </div>
      )}

      <Tabs defaultValue={isReviewer ? "all" : "mine"} className="w-full">
        <TabsList>
          {user?.employee_id && <TabsTrigger value="mine" data-testid="tab-mine">My requests</TabsTrigger>}
          {isReviewer && <TabsTrigger value="all" data-testid="tab-all">Team requests</TabsTrigger>}
        </TabsList>

        {user?.employee_id && (
          <TabsContent value="mine">
            <RequestList items={mine} statusBadge={statusBadge} />
          </TabsContent>
        )}

        {isReviewer && (
          <TabsContent value="all">
            <RequestList items={all} statusBadge={statusBadge} showEmployee onDecide={decide} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function RequestList({ items, statusBadge, showEmployee, onDecide }) {
  if (items.length === 0) return <div className="text-sm text-slate-500 py-10 text-center border border-dashed rounded-xl">No requests yet.</div>;
  return (
    <div className="space-y-3">
      {items.map(l => (
        <Card key={l.id} className="p-4 rounded-xl border-slate-200/60" data-testid={`leave-item-${l.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-50 text-[#DC2626] grid place-items-center">
                <CalendarDays className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div>
                {showEmployee && <div className="text-xs text-slate-500">{l.employee_name}</div>}
                <div className="font-semibold text-sm text-slate-900">{l.leave_type} · {l.days} day{l.days > 1 ? "s" : ""}</div>
                <div className="text-xs text-slate-500">{new Date(l.from_date).toLocaleDateString("en-IN")} → {new Date(l.to_date).toLocaleDateString("en-IN")} · {l.reason}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {statusBadge(l.status)}
              {onDecide && l.status === "Pending" && (
                <>
                  <Button size="sm" variant="outline" className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => onDecide(l.id, "Approved")} data-testid={`approve-${l.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-700 hover:bg-red-50" onClick={() => onDecide(l.id, "Rejected")} data-testid={`reject-${l.id}`}>
                    <XCircle className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Reject
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
